import type { ISocialOfferWithFavoriteChallenges } from "@api";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  CreateWithAIDropdown,
  type ILoadingButtonStyledProps,
  ModalSocialOfferFromAI,
  ResponsiveButton,
  StandardDrawerSocialOffer,
  useAuthorizer,
  useModalSocialOfferContext,
} from "@styleguide";
import { useMemo, useState } from "react";
import type { IModalSocialOfferProps } from "../ModalSocialOffer";

interface IButtonAddSocialOfferProps
  extends Omit<ILoadingButtonStyledProps, "variant" | "color" | "onClick" | "children" | "breakpoint"> {
  breakpoint?: "tablet" | "desktop";
  defaultProvider?: IModalSocialOfferProps["defaultProvider"];
  onUpdate?: (socialOffer: ISocialOfferWithFavoriteChallenges) => void;
}

export const ButtonAddSocialOffer = ({ defaultProvider, onUpdate, ...props }: IButtonAddSocialOfferProps) => {
  const openModalSocialOffer = useModalSocialOfferContext();
  const canGenerateWithAi = useAuthorizer(["offers.ia.generate"]);
  const [drawerOffer, setDrawerOffer] = useState<ISocialOfferWithFavoriteChallenges | null>(null);
  const [isOpenAIModal, setIsOpenAIModal] = useState(false);
  const aiDefaultProvider = useMemo(
    () => (defaultProvider?.name ? { _id: defaultProvider.providerId, name: defaultProvider.name } : undefined),
    [defaultProvider?.providerId, defaultProvider?.name]
  );

  const handleUpdate = (socialOffer: ISocialOfferWithFavoriteChallenges) => {
    setDrawerOffer(socialOffer);
    if (onUpdate) {
      onUpdate(socialOffer);
    }
  };

  const handleCreateManually = () => openModalSocialOffer(null, handleUpdate, false, defaultProvider);

  return (
    <>
      {canGenerateWithAi ? (
        <CreateWithAIDropdown
          label="Offre de service"
          createLabel="Créer l'offre manuellement"
          importLabel="Importer du texte pour générer l'offre via IA"
          onCreateManually={handleCreateManually}
          onImportWithAI={() => setIsOpenAIModal(true)}
          responsiveMode={Boolean(props.breakpoint)}
          responsiveBreakpoint={props.breakpoint}
        />
      ) : (
        <ResponsiveButton
          {...props}
          variant="contained"
          color="primary"
          startIcon={<AddRoundedIcon />}
          onClick={handleCreateManually}
        >
          Offre de service
        </ResponsiveButton>
      )}
      {drawerOffer && (
        <StandardDrawerSocialOffer
          open={Boolean(drawerOffer)}
          offer={drawerOffer}
          onClose={() => setDrawerOffer(null)}
          openEditModal={(offer, isDuplication) => {
            setDrawerOffer(null);
            openModalSocialOffer(offer, handleUpdate, isDuplication);
          }}
        />
      )}
      <ModalSocialOfferFromAI
        open={isOpenAIModal}
        onClose={() => setIsOpenAIModal(false)}
        defaultProvider={aiDefaultProvider}
        onCreated={(offers) => {
          for (const offer of offers) {
            onUpdate?.(offer);
          }
        }}
      />
    </>
  );
};
